import { useState } from 'react';

// Name and published state are resolved the same way AdminPortalPage does. The list is
// built from the same objects, so reading only `displayName || name` left most rows
// blank, and `status === 'published'` mislabelled forms that carry isPublished instead.
function formLabel(form) {
  return form?.displayName || form?.name || form?.title || form?.formName || 'Untitled Form';
}

function formPublished(form) {
  return Boolean(form?.isPublished || form?.published || form?.status === 'published');
}

export default function CopyFieldsModal({ sourceForm, allForms, onConfirm, onCancel }) {
  const [selectedFormId, setSelectedFormId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Filter out the source form and filter by search term
  const availableForms = allForms
    .filter(form => form.id !== sourceForm.id)
    .filter(form => {
      if (!searchTerm) return true;
      return formLabel(form).toLowerCase().includes(searchTerm.toLowerCase());
    });

  const handleConfirm = () => {
    if (selectedFormId) {
      const targetForm = allForms.find(f => f.id === selectedFormId);
      onConfirm(sourceForm.id, selectedFormId, formLabel(targetForm));
    }
  };

  return (
    <div className="custom-modal-overlay">
      <div className="custom-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="custom-modal-header">
          <h2>Copy Fields</h2>
          <button className="custom-modal-close" onClick={onCancel}>×</button>
        </div>

        <div className="custom-modal-body">
          <p style={{ marginBottom: '16px', color: '#555' }}>
            Copy all field configurations from <strong>{formLabel(sourceForm)}</strong> to:
          </p>

          <div style={{ marginBottom: '16px' }}>
            <input
              type="text"
              placeholder="Search forms..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
          </div>

          <div style={{
            maxHeight: '300px',
            overflowY: 'auto',
            border: '1px solid #ddd',
            borderRadius: '4px',
            padding: '8px'
          }}>
            {availableForms.length === 0 ? (
              <div style={{ padding: '16px', textAlign: 'center', color: '#999' }}>
                {searchTerm ? 'No forms found matching your search' : 'No other forms available'}
              </div>
            ) : (
              availableForms.map(form => (
                <div
                  key={form.id}
                  onClick={() => setSelectedFormId(form.id)}
                  style={{
                    padding: '12px',
                    marginBottom: '4px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    backgroundColor: selectedFormId === form.id ? '#e3f2fd' : '#fff',
                    border: selectedFormId === form.id ? '2px solid #2196f3' : '1px solid #eee',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedFormId !== form.id) {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedFormId !== form.id) {
                      e.currentTarget.style.backgroundColor = '#fff';
                    }
                  }}
                >
                  <div style={{ fontWeight: 500, marginBottom: '4px' }}>
                    {formLabel(form)}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    <span className={`doc-card-type ${(form.formType || (form.isInternal ? 'INTERNAL' : 'EXTERNAL')).toLowerCase()}`}
                      style={{
                        fontSize: '0.65rem',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        marginRight: '6px'
                      }}
                    >
                      {form.formType || (form.isInternal ? 'INTERNAL' : 'EXTERNAL')}
                    </span>
                    <span className={`doc-card-status ${formPublished(form) ? 'published' : 'draft'}`}
                      style={{
                        fontSize: '0.65rem',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        background: formPublished(form) ? '#d4edda' : '#fff3cd',
                        color: formPublished(form) ? '#155724' : '#856404'
                      }}
                    >
                      {formPublished(form) ? 'Published' : 'Draft'}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={{
            marginTop: '16px',
            padding: '12px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '4px',
            fontSize: '13px',
            color: '#856404'
          }}>
            <strong>Warning:</strong> This replaces all existing field configurations in
            the target form and cannot be undone. Field positions are copied as absolute
            PDF coordinates, so they only line up if both PDFs have the same page size
            and page count.
          </div>
        </div>

        <div className="custom-modal-footer">
          <button
            className="custom-modal-btn custom-modal-btn-secondary"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="custom-modal-btn custom-modal-btn-primary"
            onClick={handleConfirm}
            disabled={!selectedFormId}
            style={{
              opacity: selectedFormId ? 1 : 0.5,
              cursor: selectedFormId ? 'pointer' : 'not-allowed'
            }}
          >
            Copy Fields
          </button>
        </div>
      </div>
    </div>
  );
}
